// 会员心动 (favorites) in the 客户工作台 — what the member hearted on /client.
// Data: GET /profiles/:id/member-favorites (workspaceAPI.memberFavorites)
//   { current: [{ target, favorited_at, mutual, recommended, dated }], events: [{ action, at, target }], counts }
// The caller hides everything when the endpoint is not deployed (404 → data null).
//
//  - FavoritesPanel: ④ 资料·报告 list (badges 互相心动 / 已推荐 / 已约见, 查看档案) + collapsed 收藏记录
//  - FavoritesNudge: ① 下一步 cards for 互相心动 people not recommended yet (去推荐)
//  - favName / favWhen: shared with the 全部记录 「心动」 rows
import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Heart } from 'lucide-react';
import { resolvePhotoUrl } from '../../../utils/photoUtils';
import { toPacificWallClock } from '../../../utils/pacificTime';
import { S, card, btnGhost, btnPrimary } from './wsShared';

const asArr = (v) => (Array.isArray(v) ? v : []);
export const favName = (tg) => {
  if (!tg) return '';
  return [tg.first_name, tg.last_name].filter(Boolean).join(' ').trim() || `#${tg.id}`;
};
const favId = (tg) => (tg ? `#${tg.offline_id || tg.id}` : '');
const pad = (n) => String(n).padStart(2, '0');
// 10/16 21:05 (this year) · 2025/10/16 21:05 — on the Pacific clock
export const favWhen = (at) => {
  if (!at) return '';
  const d0 = new Date(at);
  if (isNaN(d0.getTime())) return '';
  const d = toPacificWallClock(d0);
  const md = `${d.getMonth() + 1}/${d.getDate()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  return d.getFullYear() === toPacificWallClock(new Date()).getFullYear() ? md : `${d.getFullYear()}/${md}`;
};
const facts = (tg, isZh) => [
  tg && tg.age != null ? (isZh ? `${tg.age} 岁` : `${tg.age}y`) : '',
  tg && tg.city ? tg.city : '',
].filter(Boolean).join(' · ');

const BADGE = {
  mutual: { zh: '互相心动', en: 'Mutual', fg: '#9E2B25', bg: '#F3E3E1' },
  recommended: { zh: '已推荐', en: 'Recommended', fg: '#8A6D3B', bg: '#EFEAE2' },
  dated: { zh: '已约见', en: 'Met', fg: '#3F7D5A', bg: '#E6EEE8' },
};
const badge = (k, isZh) => {
  const b = BADGE[k];
  return (
    <span key={k} style={{ fontSize: 11, fontWeight: 500, borderRadius: 2, padding: '1px 8px', color: b.fg, background: b.bg, whiteSpace: 'nowrap' }}>
      {isZh ? b.zh : b.en}
    </span>
  );
};

function Photo({ tg, size = 40 }) {
  const src = resolvePhotoUrl(tg && tg.photo);
  const ini = (favName(tg)[0] || '?').toUpperCase();
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', overflow: 'hidden', flex: '0 0 auto', background: '#EFEAE2', color: S.sub,
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 500 }}>
      {src
        ? <img src={src} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        : ini}
    </div>
  );
}

export function FavoritesPanel({ data, isZh = true, onView, busyId = null }) {
  const t = (zh, en) => (isZh ? zh : en);
  const [histOpen, setHistOpen] = useState(false);
  const current = asArr(data && data.current);
  const events = asArr(data && data.events);
  return (
    <div>
      {current.length ? current.map((c) => {
        const tg = c.target || {};
        return (
          <div key={tg.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: `1px solid ${S.line}`, flexWrap: 'wrap' }}>
            <Photo tg={tg} />
            <div style={{ flex: '1 1 220px', minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 14, fontWeight: 500, color: S.ink, overflowWrap: 'anywhere' }}>{favName(tg)}</span>
                <span style={{ fontSize: 12, color: S.mute, fontFamily: S.serif, fontStyle: 'italic' }}>{favId(tg)}</span>
                {['mutual', 'recommended', 'dated'].filter((k) => c[k]).map((k) => badge(k, isZh))}
              </div>
              <div style={{ fontSize: 12.5, color: S.sub, marginTop: 2 }}>
                {[facts(tg, isZh), c.favorited_at ? t(`心动于 ${favWhen(c.favorited_at)}`, `Hearted ${favWhen(c.favorited_at)}`) : ''].filter(Boolean).join(' · ')}
              </div>
            </div>
            {onView && (
              <button type="button" disabled={busyId === tg.id} onClick={() => onView(tg)} style={{ ...btnGhost, height: 30, cursor: busyId === tg.id ? 'wait' : 'pointer' }}>
                {t('查看档案', 'View profile')}
              </button>
            )}
          </div>
        );
      }) : (
        <div style={{ fontSize: 13, color: S.mute, padding: '8px 0' }}>{t('TA 还没有把任何人加入心动。', 'No hearts yet.')}</div>
      )}
      {events.length > 0 && (
        <>
          <button type="button" onClick={() => setHistOpen((v) => !v)}
            style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', padding: '10px 0 4px', cursor: 'pointer', fontSize: 13, fontWeight: 500, color: S.sub, fontFamily: 'inherit' }}>
            {histOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}{t('收藏记录', 'History')} <span style={{ color: S.mute }}>{events.length}</span>
          </button>
          {histOpen && events.map((e, i) => (
            <div key={`${e.action}-${e.target && e.target.id}-${e.at}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', fontSize: 13, color: S.ink, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, fontWeight: 500, borderRadius: 2, padding: '1px 6px',
                color: e.action === 'remove' ? S.sub : '#9E2B25', background: e.action === 'remove' ? '#EFEAE2' : '#F3E3E1' }}>
                {e.action === 'remove' ? t('取消', 'Removed') : t('加入', 'Added')}
              </span>
              <span style={{ overflowWrap: 'anywhere' }}>{favName(e.target)} <span style={{ color: S.mute, fontFamily: S.serif, fontStyle: 'italic', fontSize: 12 }}>{favId(e.target)}</span></span>
              <span style={{ color: S.mute, fontSize: 12 }}>{favWhen(e.at)}</span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

/** 互相心动 but no 推荐 yet → one gentle card per person (max 3). Renders nothing otherwise. */
export function FavoritesNudge({ data, isZh = true, onRecommend }) {
  const t = (zh, en) => (isZh ? zh : en);
  const list = asArr(data && data.current).filter((c) => c.mutual && !c.recommended && c.target).slice(0, 3);
  if (!list.length) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 18 }}>
      {list.map((c) => (
        <div key={c.target.id} style={{ ...card, background: '#F3E3E1', border: '1px solid rgba(158,43,37,.25)', padding: '11px 14px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Heart size={17} color="#9E2B25" fill="#E8C4C0" style={{ flex: '0 0 auto' }} />
          <Photo tg={c.target} size={30} />
          <div style={{ flex: '1 1 220px', minWidth: 0, fontSize: 14, color: S.ink, lineHeight: 1.5 }}>
            {t(`TA 和 ${favName(c.target)} 互相心动，还没推荐`, `Mutual heart with ${favName(c.target)} — not recommended yet`)}
          </div>
          {onRecommend && (
            <button type="button" onClick={() => onRecommend(c.target)} style={{ ...btnPrimary(false), height: 30 }}>{t('去推荐', 'Recommend')}</button>
          )}
        </div>
      ))}
    </div>
  );
}
